// frontend/src/components/tasks/TeamDashboard.js
// Team roll-up built from the real /dashboard/team endpoint.
// (The previous version fell back to hardcoded mock members and stats when the
// API call failed, which meant the page could show numbers that were not real.)
import React, { useState, useCallback } from 'react';
import {
    Box, Typography, Grid, Paper, Card, CardContent, LinearProgress, Avatar,
    Chip, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    IconButton, Tooltip, Alert, Tabs, Tab, Skeleton, Stack, TableSortLabel,
} from '@mui/material';
import {
    Assignment, CheckCircle, Warning, Refresh, PauseCircle, Speed,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

import useAutoRefresh from '../../hooks/useAutoRefresh';
import { getTeamRollup } from '../../services/endpoints';
import { ROLE_LABELS, isManagerRole } from '../../constants/domain';
import { avatarUrl } from '../../utils/avatar';
import { useAuth } from '../../context/AuthContext';
import TeamManagement from './TeamManagement';

const StatCard = ({ title, value, icon, color, subtitle }) => (
    <Card sx={{ height: '100%' }}>
        <CardContent>
            <Box display="flex" justifyContent="space-between" alignItems="center">
                <Typography color="textSecondary" variant="subtitle2" gutterBottom>
                    {title}
                </Typography>
                <Avatar sx={{ bgcolor: color, width: 40, height: 40 }}>{icon}</Avatar>
            </Box>
            <Typography variant="h4" fontWeight={700}>{value}</Typography>
            {subtitle && (
                <Typography variant="caption" color="text.secondary">{subtitle}</Typography>
            )}
        </CardContent>
    </Card>
);

const COLUMNS = [
    { key: 'full_name',      label: 'Engineer' },
    { key: 'role',           label: 'Role' },
    { key: 'total_parts',    label: 'Assigned',   align: 'right' },
    { key: 'in_flight',      label: 'In flight',  align: 'right' },
    { key: 'completed',      label: 'Completed',  align: 'right' },
    { key: 'on_hold',        label: 'On hold',    align: 'right' },
    { key: 'overdue',        label: 'Overdue',    align: 'right' },
    { key: 'avg_completion', label: 'Progress',   align: 'right' },
    { key: 'avg_tat',        label: 'Avg TAT (hrs)', align: 'right' },
    { key: 'total_loops',    label: 'Rework',     align: 'right' },
];

const TeamDashboard = () => {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [rows, setRows] = useState([]);
    const [error, setError] = useState(null);
    const [tab, setTab] = useState(0);
    const [sortBy, setSortBy] = useState('in_flight');
    const [sortDir, setSortDir] = useState('desc');

    const fetchTeam = useCallback(async () => {
        try {
            const res = await getTeamRollup();
            setRows(res.data || []);
            setError(null);
        } catch (err) {
            setError(err.response?.data?.message || err.message);
            setRows([]);
        }
    }, []);

    const { lastRefresh, refreshing, refresh } = useAutoRefresh(fetchTeam);

    const handleSort = (key) => {
        if (sortBy === key) setSortDir(d => (d === 'asc' ? 'desc' : 'asc'));
        else { setSortBy(key); setSortDir('desc'); }
    };

    const sorted = [...rows].sort((a, b) => {
        const av = a[sortBy], bv = b[sortBy];
        const cmp = typeof av === 'string'
            ? String(av).localeCompare(String(bv))
            : Number(av || 0) - Number(bv || 0);
        return sortDir === 'asc' ? cmp : -cmp;
    });

    // Team-wide totals, derived from the same rows so they always agree.
    const totals = rows.reduce((acc, r) => ({
        assigned: acc.assigned + Number(r.total_parts || 0),
        completed: acc.completed + Number(r.completed || 0),
        inFlight: acc.inFlight + Number(r.in_flight || 0),
        overdue: acc.overdue + Number(r.overdue || 0),
    }), { assigned: 0, completed: 0, inFlight: 0, overdue: 0 });

    const completionRate = totals.assigned
        ? Math.round((totals.completed / totals.assigned) * 100)
        : 0;

    return (
        <Box>
            <Box sx={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                mb: 2, flexWrap: 'wrap', gap: 1,
            }}>
                <Box>
                    <Typography variant="h5" fontWeight={700}>Team Dashboard</Typography>
                    <Typography variant="body2" color="text.secondary">
                        {rows.length} team member{rows.length === 1 ? '' : 's'}
                        {lastRefresh && ` — updated ${lastRefresh.toLocaleTimeString('en-GB')}`}
                    </Typography>
                </Box>
                <Tooltip title="Refresh">
                    <IconButton onClick={refresh} disabled={refreshing}><Refresh /></IconButton>
                </Tooltip>
            </Box>

            <Tabs value={tab} onChange={(e, v) => setTab(v)} sx={{ mb: 2 }}>
                <Tab label="Overview" />
                {isManagerRole(user?.role) && <Tab label="Team Management" />}
            </Tabs>

            {tab === 1 ? <TeamManagement /> : (
                <>
                    {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

                    <Grid container spacing={2} sx={{ mb: 2 }}>
                        <Grid item xs={12} sm={6} md={3}>
                            <StatCard title="Assigned parts" value={totals.assigned}
                                icon={<Assignment />} color="#1e88e5" />
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <StatCard title="Completed" value={totals.completed}
                                icon={<CheckCircle />} color="#43a047"
                                subtitle={`${completionRate}% of assigned`} />
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <StatCard title="In flight" value={totals.inFlight}
                                icon={<Speed />} color="#fb8c00" />
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <StatCard title="Overdue" value={totals.overdue}
                                icon={<Warning />} color="#e53935" />
                        </Grid>
                    </Grid>

                    <Paper>
                        <TableContainer>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        {COLUMNS.map(c => (
                                            <TableCell key={c.key} align={c.align || 'left'}
                                                sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>
                                                <TableSortLabel
                                                    active={sortBy === c.key}
                                                    direction={sortBy === c.key ? sortDir : 'asc'}
                                                    onClick={() => handleSort(c.key)}>
                                                    {c.label}
                                                </TableSortLabel>
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {refreshing && rows.length === 0 && [...Array(5)].map((_, i) => (
                                        <TableRow key={i}>
                                            <TableCell colSpan={COLUMNS.length}>
                                                <Skeleton height={30} />
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                    {!refreshing && rows.length === 0 && !error && (
                                        <TableRow>
                                            <TableCell colSpan={COLUMNS.length} align="center"
                                                sx={{ py: 4 }}>
                                                <Typography color="text.secondary">
                                                    No team members found.
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    )}
                                    {sorted.map(r => (
                                        <TableRow key={r.id} hover sx={{ cursor: 'pointer' }}
                                            onClick={() => navigate(`/parts?engineer=${r.id}`)}>
                                            <TableCell>
                                                <Stack direction="row" spacing={1} alignItems="center">
                                                    <Avatar src={avatarUrl(r.avatar_path)} sx={{
                                                        width: 28, height: 28, fontSize: 13,
                                                        bgcolor: 'primary.main',
                                                    }}>
                                                        {(r.full_name || '?').charAt(0)}
                                                    </Avatar>
                                                    <Typography variant="body2" fontWeight={600}>
                                                        {r.full_name}
                                                    </Typography>
                                                </Stack>
                                            </TableCell>
                                            <TableCell>
                                                <Chip size="small" variant="outlined"
                                                    label={ROLE_LABELS[r.role] || r.role} />
                                            </TableCell>
                                            <TableCell align="right">{r.total_parts}</TableCell>
                                            <TableCell align="right">{r.in_flight}</TableCell>
                                            <TableCell align="right">{r.completed}</TableCell>
                                            <TableCell align="right">
                                                {r.on_hold > 0
                                                    ? <Chip size="small" color="warning"
                                                        icon={<PauseCircle />} label={r.on_hold} />
                                                    : 0}
                                            </TableCell>
                                            <TableCell align="right">
                                                {r.overdue > 0
                                                    ? <Chip size="small" color="error" label={r.overdue} />
                                                    : 0}
                                            </TableCell>
                                            <TableCell align="right" sx={{ minWidth: 120 }}>
                                                <Stack direction="row" spacing={1} alignItems="center">
                                                    <LinearProgress variant="determinate"
                                                        value={Math.min(100, r.avg_completion || 0)}
                                                        sx={{ flex: 1, height: 6, borderRadius: 3 }} />
                                                    <Typography variant="caption">
                                                        {r.avg_completion || 0}%
                                                    </Typography>
                                                </Stack>
                                            </TableCell>
                                            <TableCell align="right">
                                                {r.avg_tat ? Number(r.avg_tat).toFixed(1) : '-'}
                                            </TableCell>
                                            <TableCell align="right">{r.total_loops || 0}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Paper>

                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                        Click a row to see that engineer's parts.
                    </Typography>
                </>
            )}
        </Box>
    );
};

export default TeamDashboard;
